import { useQuery } from '@apollo/client';
import { datadogLogs } from '@datadog/browser-logs';
import { CurrencyCodes } from '@onevesthq/ov-enums';
import { WorkflowContext } from '@ovComponents/4-module/workflowCompletion/workflowCompletionContext';
import { AnalyticsContext } from '@providers/analyticsProvider';
import { ovAnalyticsEvents } from '@utils/analytics/analytics';
import { currencySymbol } from '@utils/currency';
import { useToast } from 'hephaestus/web';
import { useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocalization } from '../../../../../hooks/useLocalization';
import { withEditedSecurities } from '../householdModelPortfolioDraft/householdAllocations';
import {
  DRAFT_ERROR_COPY,
  HouseholdModelPortfolioDraftSkeleton,
  HouseholdModelPortfolioDraftUnavailable,
} from '../householdModelPortfolioDraft/householdModelPortfolioDraftStates';
import { useHouseholdModelPortfolioDraft } from '../householdModelPortfolioDraft/useHouseholdModelPortfolioDraft';
import {
  ProposalNotEditableError,
  ProposalNotSubmittableError,
  useModelChangeProposal,
} from '../modelChangeProposal/useModelChangeProposal';
import { FETCH_HOUSEHOLD_MARKET_VALUE } from './reviewAllocations.queries';
import {
  type ReviewAllocationsOptions,
  ReviewAllocationsVisual,
} from './reviewAllocations.visual';

const TEST_ID = 'review-allocations';

export type ReviewAllocationsProps = {
  options: ReviewAllocationsOptions;
  clientGroupId?: string;
  onNext: (branchId?: string) => void;
  stepLoading?: boolean;
};

/**
 * The last look at the holdings before they leave for signature.
 *
 * Approving draws up the model change proposal and submits it, which is where
 * the household's edit stops being something only this workflow knows about:
 * from here it is product-service that carries it, and the workflow only learns
 * how it ended through the signing event.
 */
export const ReviewAllocations = ({
  options,
  clientGroupId,
  onNext,
  stepLoading = false,
}: ReviewAllocationsProps) => {
  const { translateBackendString } = useLocalization();
  const { workflowData, setWorkflowData } = useContext(WorkflowContext);
  const { sendAnalytic } = useContext(AnalyticsContext);
  const { enqueueToast } = useToast();
  const { t } = useTranslation(['workflowCompletions', 'shared']);
  const [submitting, setSubmitting] = useState(false);
  const {
    saveProposal,
    submitProposal,
    loading: proposalLoading,
  } = useModelChangeProposal();
  // The allocations step normally leaves the draft in context, but a completion
  // resumed straight onto this one would otherwise review nothing and still let
  // the advisor send it for signature.
  const {
    draft,
    baseline,
    loading: loadingDraft,
    error: draftError,
    retry,
  } = useHouseholdModelPortfolioDraft(clientGroupId);

  const householdClientGroupId =
    workflowData.currentHouseholdClientGroupId ?? clientGroupId;

  // The balance the target weights are read against. The review is still whole
  // without it — the weights are what is being signed — so a household whose
  // balance cannot be read loses the money columns rather than the step. The
  // query is waited out before the table is drawn so the money columns do not
  // appear after the weights.
  const { data: household, loading: loadingBalance } = useQuery(
    FETCH_HOUSEHOLD_MARKET_VALUE,
    {
      variables: { clientGroupId: householdClientGroupId },
      skip: !householdClientGroupId,
    },
  );
  const statistics = household?.fetchClientGroup?.clientGroup?.statistics;
  const householdMarketValueCents: number | undefined =
    statistics?.marketValueCents ?? undefined;
  const currency =
    currencySymbol[statistics?.currency as CurrencyCodes] ??
    currencySymbol[CurrencyCodes.CAD] ??
    '$';

  // The step before this one works in table rows, which are the newer word on
  // the holdings than the ones the draft was loaded with.
  const proposed = useMemo(
    () => (draft ? withEditedSecurities(draft) : undefined),
    [draft],
  );

  /**
   * How far the send got. Drawing the proposal up and sending it out for
   * signature are two calls, and being told the change could not be saved when
   * it is already saved would have the advisor redo work that is done.
   */
  const failed = (caught: unknown, stage: 'DRAWING_UP' | 'SENDING') => {
    if (caught instanceof ProposalNotEditableError) {
      enqueueToast(
        t('workflowCompletions:householdModelPortfolioAllocations.notEditable'),
        { variant: 'error' },
      );
      return;
    }

    // Nothing the advisor can retry: the proposal cannot say what it is
    // changing from, so product-service will not take it for signature.
    if (caught instanceof ProposalNotSubmittableError) {
      enqueueToast(
        t('workflowCompletions:householdModelPortfolioAllocations.missingData'),
        { variant: 'error' },
      );
      return;
    }

    enqueueToast(
      t(
        stage === 'SENDING'
          ? 'workflowCompletions:householdModelPortfolioAllocations.submitError'
          : 'workflowCompletions:householdModelPortfolioAllocations.saveError',
      ),
      { variant: 'error' },
    );
  };

  const approve = async () => {
    const organizationId = workflowData.entityOrganizationId;
    if (!proposed || !householdClientGroupId || !organizationId) {
      enqueueToast(
        t('workflowCompletions:householdModelPortfolioAllocations.missingData'),
        { variant: 'error' },
      );
      return;
    }

    setSubmitting(true);
    // Held outside the attempt so a failure can name the proposal it got as far
    // as, which on a first pass the context does not know about yet.
    let proposalId = workflowData.modelChangeProposalId;
    let stage: 'DRAWING_UP' | 'SENDING' = 'DRAWING_UP';
    try {
      const proposal = await saveProposal({
        proposed,
        current: baseline,
        householdId: householdClientGroupId,
        organizationId,
        modelChangeProposalId: proposalId,
        // The editing step says whether the advisor was given the name at all.
        // Without that, the proposal leaves the name of the household's copy
        // to product-service rather than proposing the one it already has.
        includeModelName:
          !!workflowData.householdIpsFieldConfig?.ipsModelName?.enabled,
      });
      proposalId = proposal.id;

      // Written before the proposal is sent, so a submit that fails leaves the
      // workflow holding the proposal it drew up rather than drawing a second
      // one product-service would refuse. The holdings go back too: the
      // proposal now says what they are, and the context would otherwise keep
      // answering with the ones the draft was loaded with. The rows are left
      // where they are, since the step that works in them is still behind us.
      await setWorkflowData((previousData) => ({
        ...previousData,
        modelPortfolioDetails: {
          ...previousData.modelPortfolioDetails,
          children: proposed.children,
        },
        modelChangeProposalId: proposal.id,
        currentHouseholdClientGroupId: householdClientGroupId,
      }));

      // Only once the workflow is holding the proposal: a context write that
      // failed leaves it with work to redo, whatever product-service has.
      stage = 'SENDING';
      await submitProposal(proposal);

      sendAnalytic(ovAnalyticsEvents.householdAllocationsProposalSubmit);
      onNext();
    } catch (caught) {
      datadogLogs.logger.error(
        'Household model change proposal submit failed',
        {
          clientGroupId: householdClientGroupId,
          modelChangeProposalId: proposalId,
          stage,
        },
        caught instanceof Error ? caught : undefined,
      );
      failed(caught, stage);
    } finally {
      setSubmitting(false);
    }
  };

  if (
    loadingDraft ||
    (!!householdClientGroupId && loadingBalance) ||
    (!draft && !draftError)
  ) {
    return (
      <HouseholdModelPortfolioDraftSkeleton
        title={translateBackendString(options?.title)}
        testId={TEST_ID}
      />
    );
  }

  if (draftError || !proposed) {
    return (
      <HouseholdModelPortfolioDraftUnavailable
        title={translateBackendString(options?.title)}
        message={t(
          `workflowCompletions:${DRAFT_ERROR_COPY[draftError ?? 'MISSING_MODEL']}`,
        )}
        testId={TEST_ID}
        onRetry={draftError === 'LOAD_FAILED' ? retry : undefined}
      />
    );
  }

  return (
    <ReviewAllocationsVisual
      options={options}
      proposed={proposed}
      current={baseline}
      householdMarketValueCents={householdMarketValueCents}
      currency={currency}
      loading={stepLoading || submitting || proposalLoading}
      onApprove={approve}
    />
  );
};

export default ReviewAllocations;
